import {useNavigation} from '@react-navigation/native';
import {MoodLightDto} from 'puffco-api-axios-client';
import React from 'react';
import {useSelector} from 'react-redux';

import {Alert} from '../../components/Alert';
import {Alerts, interpolate} from '../../constants/Strings';
import {analytics} from '../../services/analytics';
import {withAsyncWrapper} from '../api/asyncWrapper';
import {allAndCurrentDeviceMoodLightsSelector} from '../redux/slices/moodLightSlice';
import {allHeatProfilesMapSelector} from '../redux/slices/profilesSlice';
import {HeatProfile} from '../types';
import {useAsyncFn} from './useAsyncFn';
import {useUpsertHeatProfiles} from './useUpsertHeatProfiles';

interface Options {
  heatProfileId: string;
  moodLightId: string;
}

export const useAssignMoodLightToHeatProfile = () => {
  const navigation = useNavigation();

  const profiles = useSelector(allHeatProfilesMapSelector);
  const moodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);

  const [, upsertHeatProfiles] = useUpsertHeatProfiles();

  const onSuccess = React.useCallback(
    ({profile, moodLight}: {profile: HeatProfile; moodLight: MoodLightDto}) => {
      analytics.trackEvent('mood light assign', {
        profile_id: profile.id,
        profile_name: profile.name,
        mood_light_id: moodLight.id,
        mood_light_type: moodLight.category,
      });

      Alert.alert(
        Alerts.MOOD_ASSIGNED,
        interpolate(Alerts.MOOD_ASSIGNED_BODY, {
          moodName: moodLight.name,
          profileName: profile.name,
        }),
      );

      navigation.goBack();
    },
    [navigation],
  );

  const [{loading}, assign] = useAsyncFn(
    withAsyncWrapper(
      async ({heatProfileId, moodLightId}: Options) => {
        const profile = profiles[heatProfileId];
        const moodLight = moodLights[moodLightId];

        if (!profile || !moodLight)
          throw new Error(
            'An error occured while assigning the mood light to heat profile',
          );

        const assigned = {
          ...profile,
          light: moodLight,
        };

        await upsertHeatProfiles([assigned]);

        return {profile, moodLight};
      },
      {
        onSuccess,
        onError,
      },
    ),
    [profiles, moodLights, upsertHeatProfiles],
  );

  return React.useMemo(
    () => [{isLoading: loading}, assign] as const,
    [loading, assign],
  );
};

const onError = (error: Error) => {
  Alert.alert(Alerts.MOOD_LIGHT_ASSIGN_ERROR_TITLE, error.message);
};
